import { importLibrary } from '@googlemaps/js-api-loader'
import type { PlacesProvider, PlaceSuggestion, ResolvedPlace, SearchPlacesOptions } from '@gpx-spa/geo-domain'
import { loadGoogleMapsPlacesScript } from '../script/load-google-maps-script.js'
import { postalAddressFromComponents } from './google-postal-address.js'

export interface GoogleMapsJsPlacesProviderOptions {
  /** Browser key (e.g. `VITE_GOOGLE_MAPS_API_KEY`). Enable **Maps JavaScript API** and **Places API (New)** in Google Cloud. */
  getApiKey: () => string
  /** Limit suggestions to these CLDR region codes, e.g. `['us']`. */
  includedRegionCodes?: string[]
}

function readLatLng(loc: google.maps.LatLng | google.maps.LatLngLiteral | null | undefined): {
  lat: number
  lng: number
} {
  if (!loc) {
    throw new Error('Place has no location')
  }
  if (typeof (loc as google.maps.LatLng).lat === 'function') {
    const l = loc as google.maps.LatLng
    return { lat: l.lat(), lng: l.lng() }
  }
  const o = loc as google.maps.LatLngLiteral
  return { lat: o.lat, lng: o.lng }
}

/**
 * {@link PlacesProvider} using **Places API (New)** in the Maps JavaScript API:
 * `AutocompleteSuggestion.fetchAutocompleteSuggestions` + `Place.fetchFields` (not legacy `AutocompleteService`).
 *
 * @see https://developers.google.com/maps/documentation/javascript/places-migration-autocomplete
 */
export function createGoogleMapsJsPlacesProvider(
  options: GoogleMapsJsPlacesProviderOptions
): PlacesProvider {
  const { getApiKey, includedRegionCodes } = options

  let sessionToken: google.maps.places.AutocompleteSessionToken | null = null

  return {
    async searchPlaces(query: string, searchOptions?: SearchPlacesOptions): Promise<PlaceSuggestion[]> {
      const apiKey = getApiKey().trim()
      const input = query.trim()
      if (!apiKey || !input) {
        return []
      }
      if (searchOptions?.signal?.aborted) {
        return []
      }

      await loadGoogleMapsPlacesScript(apiKey)
      const places = await importLibrary('places')

      if (!sessionToken) {
        sessionToken = new places.AutocompleteSessionToken()
      }

      const { suggestions } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken,
        ...(includedRegionCodes ? { includedRegionCodes } : {}),
      })

      if (searchOptions?.signal?.aborted) {
        return []
      }

      const out: PlaceSuggestion[] = []
      for (const s of suggestions) {
        const pp = s.placePrediction
        if (!pp) {
          continue
        }
        const label = pp.text?.text ?? ''
        const subtitle = pp.secondaryText?.text ?? undefined
        out.push({
          id: pp.placeId,
          label,
          subtitle,
        })
      }
      return out
    },

    async resolvePlace(suggestion: PlaceSuggestion): Promise<ResolvedPlace> {
      const apiKey = getApiKey().trim()
      if (!apiKey) {
        throw new Error('Google Maps API key is not set')
      }

      await loadGoogleMapsPlacesScript(apiKey)
      const places = await importLibrary('places')

      const place = new places.Place({ id: suggestion.id })

      try {
        await place.fetchFields({
          fields: ['location', 'formattedAddress', 'id', 'addressComponents'],
        })
      } finally {
        sessionToken = null
      }

      const coords = readLatLng(place.location)
      return {
        coordinates: coords,
        formattedAddress: place.formattedAddress ?? suggestion.label,
        providerPlaceId: place.id ?? suggestion.id,
        postalAddress: postalAddressFromComponents(place.addressComponents ?? []),
      }
    },
  }
}
